import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EnhancementView from './EnhancementView';
import QuoteParametersForm, { matchCostCentre, QuoteParametersEditor, QuoteParametersValues } from './QuoteParametersForm';
import FinalisedQuotesView from './FinalisedQuotesView';
import LabourView from './LabourView';
import ReviewView from './ReviewView';
import FinalView from './FinalView';
import * as quoteApi from './quoteApi';
import { CostCentre, Quote, SourceJob } from './quoteApi';

type ListView = 'active' | 'finalised' | 'all';

const compactTabsSx = {
  minHeight: 32,
  '& .MuiTab-root': {
    minHeight: 32,
    py: 0.25,
    px: 1.25,
    fontSize: 13,
    textTransform: 'none' as const,
    minWidth: 0,
  },
  '& .MuiTabs-indicator': { height: 2 },
};

const stageTabsSx = {
  mb: 2,
  minHeight: 48,
  '& .MuiTab-root': {
    minHeight: 48,
    py: 1.25,
    px: 2,
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none' as const,
    minWidth: 0,
  },
  '& .Mui-selected .step-num': {
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
  },
};

const stepForQuote = (quote: Quote) => {
  if (quote.status === 'finalised') return 3;
  if (quote.status === 'review') return 2;
  if (quote.status === 'labour') return 1;
  return 0;
};

const quoteHours = (quote: Quote) => {
  const value = quote.labour?.final_hrs;
  return typeof value === 'number' ? value.toFixed(2) : '—';
};

const DEFAULT_LOCATIONS = ['Plate 1', 'Door', 'Enclosure'];

const STEPS = [
  { id: 'enhancing', label: 'Enhancement' },
  { id: 'labour', label: 'Labour' },
  { id: 'review', label: 'Review' },
  { id: 'finalised', label: 'Final' },
] as const;

const statusColor = (status: string) => {
  if (status === 'finalised') return 'success';
  if (status === 'enhancing') return 'info';
  return 'default';
};

const EstimatorView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [openQuote, setOpenQuote] = useState<Quote | null>(null);
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [jobs, setJobs] = useState<SourceJob[]>([]);
  const [centres, setCentres] = useState<CostCentre[]>([]);
  const [jobId, setJobId] = useState('');
  const [costCentre, setCostCentre] = useState('');
  const [source, setSource] = useState<'reconciliation' | 'erp_upload' | 'no_tag'>('reconciliation');
  const [createStep, setCreateStep] = useState(0);
  const [customer, setCustomer] = useState('');
  const [notes, setNotes] = useState('');
  const [enclosureH, setEnclosureH] = useState('');
  const [enclosureW, setEnclosureW] = useState('');
  const [enclosureD, setEnclosureD] = useState('');
  const [cableType, setCableType] = useState('Auto');
  const [volumeFactor, setVolumeFactor] = useState(1);
  const [preDrilled, setPreDrilled] = useState(false);
  const [additionalIdents, setAdditionalIdents] = useState(false);
  const [locations, setLocations] = useState<string[]>(DEFAULT_LOCATIONS);
  const [locationDraft, setLocationDraft] = useState('');
  const [creating, setCreating] = useState(false);
  const [paramsEditOpen, setParamsEditOpen] = useState(false);
  const [paramsSaving, setParamsSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [stageBusy, setStageBusy] = useState(false);

  const parameterValues: QuoteParametersValues = {
    customer,
    costCentre,
    notes,
    enclosureH,
    enclosureW,
    enclosureD,
    cableType,
    volumeFactor,
    preDrilled,
    additionalIdents,
    locations,
    locationDraft,
  };

  const patchParameters = (patch: Partial<QuoteParametersValues>) => {
    if (patch.customer !== undefined) setCustomer(patch.customer);
    if (patch.costCentre !== undefined) setCostCentre(patch.costCentre);
    if (patch.notes !== undefined) setNotes(patch.notes);
    if (patch.enclosureH !== undefined) setEnclosureH(patch.enclosureH);
    if (patch.enclosureW !== undefined) setEnclosureW(patch.enclosureW);
    if (patch.enclosureD !== undefined) setEnclosureD(patch.enclosureD);
    if (patch.cableType !== undefined) setCableType(patch.cableType);
    if (patch.volumeFactor !== undefined) setVolumeFactor(patch.volumeFactor);
    if (patch.preDrilled !== undefined) setPreDrilled(patch.preDrilled);
    if (patch.additionalIdents !== undefined) setAdditionalIdents(patch.additionalIdents);
    if (patch.locations !== undefined) setLocations(patch.locations);
    if (patch.locationDraft !== undefined) setLocationDraft(patch.locationDraft);
  };

  const fillParametersFromQuote = (quote: Quote) => {
    setCustomer(quote.customer || '');
    setCostCentre(quote.cost_centre || '');
    setNotes(quote.notes || '');
    setEnclosureH(quote.enclosure_h != null ? String(quote.enclosure_h) : '');
    setEnclosureW(quote.enclosure_w != null ? String(quote.enclosure_w) : '');
    setEnclosureD(quote.enclosure_d != null ? String(quote.enclosure_d) : '');
    setCableType(quote.cable_type || 'Auto');
    setVolumeFactor(quote.volume_factor != null ? quote.volume_factor : 1);
    setPreDrilled(Boolean(quote.pre_drilled));
    setAdditionalIdents(Boolean(quote.additional_idents));
    setLocations(quote.mounting_locations?.length ? quote.mounting_locations : DEFAULT_LOCATIONS);
    setLocationDraft('');
  };
  const view = (['active', 'finalised', 'all'].includes(searchParams.get('view') || '')
    ? searchParams.get('view')
    : 'active') as ListView;
  const setView = (next: ListView) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'active') params.delete('view');
    else params.set('view', next);
    setSearchParams(params, { replace: true });
  };

  const openExisting = (quote: Quote) => {
    setOpenQuote(quote);
    setStep(stepForQuote(quote));
  };

  const activeQuotes = quotes.filter((quote) => quote.status !== 'finalised');
  const finalisedQuotes = quotes.filter((quote) => quote.status === 'finalised');
  const visibleQuotes = useMemo(() => {
    const pool = view === 'finalised' ? finalisedQuotes : view === 'active' ? activeQuotes : quotes;
    const needle = query.trim().toLowerCase();
    if (!needle) return pool;
    return pool.filter((quote) =>
      [quote.quote_ref, quote.customer, quote.notes, quote.cost_centre, quote.status]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle))
    );
  }, [quotes, view, query, activeQuotes, finalisedQuotes]);

  const loadQuotes = async () => {
    const data = await quoteApi.listQuotes();
    setQuotes(data);
  };

  useEffect(() => {
    loadQuotes().catch((err) => setError(err.message || 'Failed to load quotes'));
  }, []);

  const openNew = async () => {
    setError('');
    const [jobList, centreList] = await Promise.all([quoteApi.listSourceJobs(), quoteApi.listCostCentres()]);
    setJobs(jobList);
    setCentres(centreList);
    const partner = jobList[0]?.customer || '';
    setJobId(jobList[0]?.job_id || '');
    setCustomer(partner);
    setCostCentre(matchCostCentre(partner, centreList));
    setNotes('');
    setEnclosureH('');
    setEnclosureW('');
    setEnclosureD('');
    setCableType('Auto');
    setVolumeFactor(1);
    setPreDrilled(false);
    setAdditionalIdents(false);
    setLocations(DEFAULT_LOCATIONS);
    setLocationDraft('');
    setCreateStep(0);
    setSource('reconciliation');
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!costCentre) return;
    if (source === 'reconciliation' && !jobId) return;
    setCreating(true);
    try {
      const quote = await quoteApi.createQuote({
        job_id: source === 'reconciliation' ? jobId : undefined,
        cost_centre: costCentre,
        source,
        rows: [],
        customer: customer.trim() || undefined,
        notes: notes.trim() || undefined,
        enclosure_h: enclosureH ? Number(enclosureH) : undefined,
        enclosure_w: enclosureW ? Number(enclosureW) : undefined,
        enclosure_d: enclosureD ? Number(enclosureD) : undefined,
        cable_type: cableType,
        volume_factor: volumeFactor,
        pre_drilled: preDrilled,
        additional_idents: additionalIdents,
        mounting_locations: locations,
      });
      setDialogOpen(false);
      await loadQuotes();
      openExisting(quote);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to create quote');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async (quote: Quote, event: React.MouseEvent) => {
    event.stopPropagation();
    const copied = await quoteApi.copyQuote(quote.id);
    await loadQuotes();
    openExisting(copied);
  };

  const handleDelete = async (quote: Quote, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!window.confirm(`Delete ${quote.quote_ref}?`)) return;
    await quoteApi.deleteQuote(quote.id);
    if (openQuote?.id === quote.id) setOpenQuote(null);
    await loadQuotes();
  };

  const handleMarkComplete = async () => {
    if (!openQuote) return;
    setStageBusy(true);
    setError('');
    try {
      const updated = await quoteApi.markEnhancementComplete(openQuote.id);
      setOpenQuote(updated);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to mark complete');
    } finally {
      setStageBusy(false);
    }
  };

  const openParamsEdit = async () => {
    if (!openQuote) return;
    setError('');
    try {
      const centreList = centres.length ? centres : await quoteApi.listCostCentres();
      if (!centres.length) setCentres(centreList);
    } catch {
      /* cost centre API is optional */
    }
    fillParametersFromQuote(openQuote);
    setParamsEditOpen(true);
  };

  const handleSaveParams = async (values: QuoteParametersValues) => {
    if (!openQuote) return;
    setParamsSaving(true);
    setError('');
    try {
      const updated = await quoteApi.updateQuote(openQuote.id, {
        customer: values.customer.trim() || undefined,
        cost_centre: values.costCentre,
        notes: values.notes.trim() || undefined,
        enclosure_h: values.enclosureH ? Number(values.enclosureH) : null,
        enclosure_w: values.enclosureW ? Number(values.enclosureW) : null,
        enclosure_d: values.enclosureD ? Number(values.enclosureD) : null,
        cable_type: values.cableType,
        volume_factor: values.volumeFactor,
        pre_drilled: values.preDrilled,
        additional_idents: values.additionalIdents,
        mounting_locations: values.locations,
      });
      setOpenQuote(updated);
      setParamsEditOpen(false);
      if (updated.status !== 'enhancing') {
        await quoteApi.calculateLabour(updated.id);
        setOpenQuote(await quoteApi.getQuote(updated.id));
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save parameters');
    } finally {
      setParamsSaving(false);
    }
  };

  if (openQuote) {
    const locked = openQuote.status === 'finalised';
    return (
      <Box sx={{ px: 2, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <IconButton size="small" onClick={() => { setOpenQuote(null); loadQuotes(); }} sx={{ p: 0.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {openQuote.quote_ref}
          </Typography>
          <Chip size="small" label={`v${openQuote.version}`} variant="outlined" sx={{ height: 22 }} />
          <Chip size="small" label={openQuote.status} color={statusColor(openQuote.status) as any} sx={{ height: 22 }} />
          <Typography variant="caption" color="text.secondary">
            {openQuote.customer} · {openQuote.cost_centre || 'No cost centre'}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            disabled={locked}
            onClick={openParamsEdit}
          >
            Edit parameters
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            color="secondary"
            variant={openQuote.enhancement_complete ? 'contained' : 'outlined'}
            startIcon={<DownloadIcon />}
            disabled={!openQuote.enhancement_complete}
            href={openQuote.enhancement_complete ? quoteApi.exportUrl(openQuote.id, 'bom') : undefined}
            sx={{
              '&.Mui-disabled': {
                color: 'text.secondary',
                borderColor: 'grey.400',
                bgcolor: 'grey.100',
                opacity: 1,
              },
            }}
          >
            Export BOM
          </Button>
          {openQuote.enhancement_complete ? (
            <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Enhancement complete" sx={{ height: 28 }} />
          ) : (
            <Button
              size="small"
              color="success"
              variant="contained"
              startIcon={<CheckCircleIcon />}
              disabled={locked || stageBusy}
              onClick={handleMarkComplete}
            >
              Mark complete
            </Button>
          )}
        </Box>

        <Tabs
          value={step}
          onChange={(_, next) => {
            if (next >= 2 && openQuote.labour_ready === false) {
              setError('Complete required labour inputs before Review.');
              setStep(1);
              return;
            }
            setStep(next);
          }}
          sx={stageTabsSx}
        >
          {STEPS.map((item, index) => (
            <Tab
              key={item.id}
              value={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar
                    className="step-num"
                    sx={{
                      width: 22,
                      height: 22,
                      fontSize: 12,
                      fontWeight: 700,
                      bgcolor: 'grey.300',
                      color: 'text.primary',
                    }}
                  >
                    {index + 1}
                  </Avatar>
                  {item.label}
                </Box>
              }
            />
          ))}
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box key={openQuote.id}>
          {step === 0 && (
            <EnhancementView
              quote={openQuote}
              onQuoteChange={(q) => setOpenQuote(q)}
            />
          )}
          {step === 1 && (
            <LabourView quote={openQuote} onQuoteChange={setOpenQuote} />
          )}
          {step === 2 && (
            <ReviewView
              quote={openQuote}
              onQuoteChange={setOpenQuote}
              onCopied={(q) => { setOpenQuote(q); setStep(0); }}
            />
          )}
          {step === 3 && (
            <FinalView
              quote={openQuote}
              onQuoteChange={setOpenQuote}
              onCopied={(q) => openExisting(q)}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            disabled={step === 0}
            onClick={() => setStep((current) => Math.max(0, current - 1))}
          >
            Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button
              size="small"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              disabled={step === 1 && openQuote.labour_ready === false}
              onClick={() => {
                if (step === 1 && openQuote.labour_ready === false) {
                  setError('Complete required labour inputs before Review.');
                  return;
                }
                setStep((current) => Math.min(STEPS.length - 1, current + 1));
              }}
            >
              Next: {STEPS[step + 1].label}
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Last step — mark as final when ready.
            </Typography>
          )}
        </Box>

        <Dialog open={paramsEditOpen} onClose={() => setParamsEditOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Edit parameters · {openQuote.quote_ref}</DialogTitle>
          {paramsEditOpen && (
            <QuoteParametersEditor
              key={`${openQuote.id}-${openQuote.updated_at || ''}`}
              initial={parameterValues}
              centres={centres}
              saving={paramsSaving}
              onCancel={() => setParamsEditOpen(false)}
              onSave={handleSaveParams}
            />
          )}
        </Dialog>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Estimator
          </Typography>
        </Box>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          New estimate
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
        <Tabs value={view} onChange={(_, next) => setView(next)} sx={compactTabsSx}>
          <Tab value="active" label={`In progress (${activeQuotes.length})`} />
          <Tab value="finalised" label={`Finalised (${finalisedQuotes.length})`} />
          <Tab value="all" label={`All (${quotes.length})`} />
        </Tabs>
        {view !== 'finalised' && (
          <TextField size="small" placeholder="Search" value={query} onChange={(e) => setQuery(e.target.value)} sx={{ minWidth: 200 }} />
        )}
      </Box>

      {view === 'finalised' ? (
        <FinalisedQuotesView quotes={quotes} onOpen={openExisting} onCopy={handleCopy} />
      ) : (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Quote</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Source</TableCell>
              <TableCell align="right">Hours</TableCell>
              <TableCell align="right">Lines</TableCell>
              <TableCell>Created by</TableCell>
              <TableCell>Last modified by</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleQuotes.map((quote) => (
              <TableRow key={quote.id} hover sx={{ cursor: 'pointer' }} onClick={() => openExisting(quote)}>
                <TableCell sx={{ fontWeight: 600 }}>
                  {quote.quote_ref} <Typography component="span" variant="caption" color="text.secondary">v{quote.version}</Typography>
                </TableCell>
                <TableCell>{quote.customer || '—'}</TableCell>
                <TableCell>{quote.notes || '—'}</TableCell>
                <TableCell>
                  <Chip size="small" label={quote.status} color={statusColor(quote.status) as any} />
                </TableCell>
                <TableCell>{quote.source}</TableCell>
                <TableCell align="right">{quoteHours(quote)}</TableCell>
                <TableCell align="right">{quote.line_count}</TableCell>
                <TableCell>{quote.created_by || '—'}</TableCell>
                <TableCell>{quote.updated_by || quote.created_by || '—'}</TableCell>
                <TableCell>{quote.updated_at ? new Date(quote.updated_at).toLocaleString() : '—'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={(e) => handleCopy(quote, e)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" disabled={quote.status === 'finalised'} onClick={(e) => handleDelete(quote, e)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {visibleQuotes.length === 0 && (
              <TableRow>
                <TableCell colSpan={11}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    {quotes.length === 0
                      ? 'No quotes yet. Create one from a reconciliation job that has confirmed lines.'
                      : 'No in-progress estimates. Finalised quotes are on the Finalised tab.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New estimate · {createStep === 0 ? 'Parameters' : 'BOM source'}</DialogTitle>
        <DialogContent>
          {createStep === 0 ? (
            <QuoteParametersForm values={parameterValues} centres={centres} onChange={patchParameters} />
          ) : (
            <Box sx={{ pt: 1 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Source</InputLabel>
                <Select value={source} label="Source" onChange={(e) => setSource(e.target.value as typeof source)}>
                  <MenuItem value="reconciliation">Confirmed reconciliation job</MenuItem>
                  <MenuItem value="erp_upload">ERP Excel upload</MenuItem>
                  <MenuItem value="no_tag">Manual entry (no import)</MenuItem>
                </Select>
                <FormHelperText>
                  {source === 'no_tag'
                    ? 'Manual entry. No file is imported — type each BOM line on the Enhancement screen.'
                    : 'Choose where the BOM comes from.'}
                </FormHelperText>
              </FormControl>
              {source === 'reconciliation' && jobs.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No jobs with confirmed reconciliation lines. Confirm lines on Reconciliation first.
                </Alert>
              )}
              {source === 'reconciliation' && (
                <Autocomplete
                  size="small"
                  sx={{ mb: 2 }}
                  disabled={jobs.length === 0}
                  options={jobs.filter((job) => {
                    const partner = customer.trim().toLowerCase();
                    if (!partner) return true;
                    const name = (job.customer || '').toLowerCase();
                    return name.includes(partner) || partner.includes(name);
                  })}
                  value={jobs.find((job) => job.job_id === jobId) || null}
                  onChange={(_event, job) => {
                    setJobId(job?.job_id || '');
                    if (job?.customer) setCustomer(job.customer);
                  }}
                  getOptionLabel={(job) => {
                    const when = job.updated_at || job.created_at;
                    const date = when ? new Date(when).toLocaleDateString('en-GB') : '';
                    return [
                      job.customer || 'No partner',
                      job.description || job.pdf_filename,
                      `${job.confirmed_count} confirmed`,
                      date,
                      job.created_by || '',
                    ].filter(Boolean).join(' · ');
                  }}
                  isOptionEqualToValue={(option, value) => option.job_id === value.job_id}
                  renderInput={(params) => (
                    <TextField {...params} label="Reconciliation job" placeholder="Search partner, description or date" />
                  )}
                />
              )}
              {source === 'erp_upload' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Creates an empty estimate. Import Excel on Enhancement — headers can sit below title rows. Expected columns: No., Vendor Item No., Vendor No., Description, Qty, BUoM.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          {createStep === 1 && <Button onClick={() => setCreateStep(0)}>Back</Button>}
          {createStep === 0 ? (
            <Button variant="contained" onClick={() => setCreateStep(1)} disabled={!costCentre}>
              Next: BOM source
            </Button>
          ) : (
            <Button variant="contained" onClick={handleCreate} disabled={!costCentre || creating || (source === 'reconciliation' && !jobId)}>
              Create
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EstimatorView;
